import { bbox } from "@turf/turf";
import { MapLayerConfig } from "../types";
import { apiFetch, REACT_APP_API_ROOT } from "./api";
import { fetchPlaceById } from "./mapSearchUtils";
import { wktToGeoJSON } from "./mapSearchUtils";
import wellknown from 'wellknown';

/**
 * Smart zoom options for viewport-aware zooming
 */
export interface SmartZoomOptions {
  center: [number, number];
  geometry?: any;
  map: any;
  duration?: number;
  defaultZoom?: number;
  fromMapClick?: boolean;
  flyToMethod?: (options: any) => void; // For using centralized flying methods
  fitBoundsMethod?: (bounds: any, options: any) => void; // For using centralized fitBounds methods
}

/**
 * Check if coordinates are within current map viewport
 */
export const isWithinViewport = (coordinates: [number, number], map: any): boolean => {
  if (!map || !coordinates) return false;
  
  try {
    const bounds = map.getBounds();
    const [lng, lat] = coordinates;
    
    return lng >= bounds.getWest() && 
           lng <= bounds.getEast() && 
           lat >= bounds.getSouth() && 
           lat <= bounds.getNorth();
  } catch (error) {
    console.warn('[MAP-ZOOM] Error checking viewport bounds:', error);
    return false; // Default to false (treat as far away) on error
  }
};

/**
 * Smart zoom function that respects viewport and current zoom level
 * Only preserves zoom for nearby features, allows normal zoom for distant ones
 */
export const smartZoomToLocation = (options: SmartZoomOptions): void => {
  const {
    center,
    geometry,
    map,
    duration = 2000,
    defaultZoom = 14,
    fromMapClick = false,
    flyToMethod,
    fitBoundsMethod
  } = options;

  const currentZoom = map.getZoom();
  const isNearby = isWithinViewport(center, map);
  
  // Smart zoom logic: only preserve zoom level for nearby features
  // For far away features, allow normal zoom behavior
  const shouldPreserveZoom = isNearby && currentZoom >= defaultZoom;
  
  console.log('[MAP-ZOOM] Smart zoom decision:', {
    center,
    currentZoom,
    defaultZoom,
    isNearby,
    shouldPreserveZoom,
    fromMapClick
  });

  if (geometry) {
    try {
      // Handle WKT strings
      let processedGeometry = geometry;
      if (typeof geometry === 'string') {
        processedGeometry = wktToGeoJSON(geometry);
        if (!processedGeometry) {
          throw new Error(`Failed to parse WKT geometry: ${geometry}`);
        }
      }

      // Validate geometry structure
      if (!processedGeometry || typeof processedGeometry !== 'object' ||
          !processedGeometry.type || !processedGeometry.coordinates) {
        throw new Error('Invalid geometry structure');
      }

      const boundingBox = bbox(processedGeometry);
      
      // Check if it's a very small bounding box (point-like feature)
      const width = boundingBox[2] - boundingBox[0];
      const height = boundingBox[3] - boundingBox[1];
      const isVerySmall = width < 0.001 && height < 0.001;
      
              if (isVerySmall) {
          // Point-like feature: use flyTo with smart zoom and tilt
          const targetZoom = shouldPreserveZoom ? currentZoom : defaultZoom;
          const flyToOptions = {
            center,
            zoom: targetZoom,
            pitch: targetZoom > 13.5 ? 60 : 0, // Add tilt for close-up point features
            duration,
            essential: true
          };

        if (fromMapClick || !flyToMethod) {
          // Use regular map methods for map clicks or when no centralized method provided
          map.flyTo(flyToOptions);
        } else {
          // Use centralized flying method (closes popups)
          flyToMethod(flyToOptions);
        }
      } else {
        // Larger feature: use fitBounds with smart maxZoom
        const maxZoom = shouldPreserveZoom ? currentZoom : defaultZoom;
        const bounds = [
          [boundingBox[0], boundingBox[1]], // southwest
          [boundingBox[2], boundingBox[3]]  // northeast
        ];
        const fitBoundsOptions = {
          padding: fromMapClick ? 40 : 50,
          maxZoom,
          duration
        };

        if (fromMapClick || !fitBoundsMethod) {
          // Use regular map methods for map clicks or when no centralized method provided
          map.fitBounds(bounds, fitBoundsOptions);
        } else {
          // Use centralized fitBounds method (closes popups)
          fitBoundsMethod(bounds, fitBoundsOptions);
        }
      }
    } catch (error) {
      console.error('[MAP-ZOOM] Error with geometry-based zoom, falling back to flyTo:', error);
      // Fallback to simple flyTo with tilt
      const targetZoom = shouldPreserveZoom ? currentZoom : defaultZoom;
      const flyToOptions = {
        center,
        zoom: targetZoom,
        pitch: targetZoom > 13.5 ? 60 : 0, // Add tilt for close-up views
        duration,
        essential: true
      };

      if (fromMapClick || !flyToMethod) {
        map.flyTo(flyToOptions);
      } else {
        flyToMethod(flyToOptions);
      }
    }
  } else {
    // No geometry: simple flyTo with smart zoom and tilt
    const targetZoom = shouldPreserveZoom ? currentZoom : defaultZoom;
    const flyToOptions = {
      center,
      zoom: targetZoom,
      pitch: targetZoom > 13.5 ? 60 : 0, // Add tilt for close-up views
      duration,
      essential: true
    };

    if (fromMapClick || !flyToMethod) {
      map.flyTo(flyToOptions);
    } else {
      flyToMethod(flyToOptions);
    }
  }
};

export interface ZoomPlaceData {
  feature: {
    type: 'Feature';
    geometry: any;
    properties: any;
    layer: { id: string };
  };
  center: [number, number];
  zoom: number;
}

/**
 * Find layer config with flexible matching (exact, case-insensitive, partial)
 */
export const findLayerWithFlexibleMatching = (
  layers: MapLayerConfig[], 
  layerId: string
): MapLayerConfig | null => {
  console.log("[MAP-ZOOM] Looking for layer:", layerId);
  
  // Try exact match first
  let layerConfig = layers.find(layer => layer.id === layerId);
  
  if (!layerConfig) {
    console.log("[MAP-ZOOM] Exact layer match failed, trying flexible matching");
    
    // Try case-insensitive match
    layerConfig = layers.find(layer => 
      layer.id.toLowerCase() === layerId.toLowerCase()
    );
    
    // Try partial match (contains)
    if (!layerConfig) {
      layerConfig = layers.find(layer => 
        layer.id.toLowerCase().includes(layerId.toLowerCase()) || 
        layerId.toLowerCase().includes(layer.id.toLowerCase())
      );
    }
    
    if (layerConfig) {
      console.log("[MAP-ZOOM] Found matching layer with flexible search:", layerConfig.id);
    }
  }
  
  return layerConfig || null;
};

/**
 * Extract coordinates from various item formats, including WKT geometry strings
 */
export const extractCoordinatesFromItem = (item: any): [number, number] | null => {
  // Always return [longitude, latitude] format
  
  // Try geometry objects first
  if (item.geometry && typeof item.geometry === 'object' && item.geometry.coordinates) {
    const [lng, lat] = item.geometry.coordinates;
    if (typeof lng === 'number' && typeof lat === 'number' && 
        !isNaN(lng) && !isNaN(lat) &&
        lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
      return [lng, lat];
    }
  }
  
  if (item._geometry && typeof item._geometry === 'object' && item._geometry.coordinates) {
    const [lng, lat] = item._geometry.coordinates;
    if (typeof lng === 'number' && typeof lat === 'number' && 
        !isNaN(lng) && !isNaN(lat) &&
        lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
      return [lng, lat];
    }
  }
  
  // Try WKT geometry strings and parse to GeoJSON
  if (item.geometry && typeof item.geometry === 'string' && item.geometry.trim()) {
    try {
      const geoJSON = wellknown.parse(item.geometry);
      if (geoJSON) {
        const { hasValidCoordinates, center } = validateAndProcessGeometry(geoJSON);
        if (hasValidCoordinates) {
          return center;
        }
      }
    } catch (error) {
      console.error("[MAP-ZOOM] Error parsing WKT geometry:", error);
    }
  }
  
  // Try _geometry WKT strings
  if (item._geometry && typeof item._geometry === 'string' && item._geometry.trim()) {
    try {
      const geoJSON = wellknown.parse(item._geometry);
      if (geoJSON) {
        const { hasValidCoordinates, center } = validateAndProcessGeometry(geoJSON);
        if (hasValidCoordinates) {
          return center;
        }
      }
    } catch (error) {
      console.error("[MAP-ZOOM] Error parsing _geometry WKT:", error);
    }
  }
  
  // Try direct coordinate properties
  if (item.longitude && item.latitude) {
    const lng = parseFloat(item.longitude);
    const lat = parseFloat(item.latitude);
    if (lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
      return [lng, lat];
    }
  }
  
  if (item.lng && item.lat) {
    const lng = parseFloat(item.lng);
    const lat = parseFloat(item.lat);
    if (lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
      return [lng, lat];
    }
  }
  
  if (item.coordinate_longitude && item.coordinate_latitude) {
    const lng = parseFloat(item.coordinate_longitude);
    const lat = parseFloat(item.coordinate_latitude);
    if (lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
      return [lng, lat];
    }
  }
  
  return null;
};

/**
 * Validate and process geometry, extracting center coordinates
 */
export const validateAndProcessGeometry = (geoJSONGeometry: any): {
  hasValidCoordinates: boolean;
  center: [number, number];
  zoom: number;
} => {
  let center: [number, number] = [0, 0];
  let zoom = 7;
  let hasValidCoordinates = false;
  
  if (!geoJSONGeometry) {
    return { hasValidCoordinates, center, zoom };
  }
  
  try {
    if (geoJSONGeometry.type === "Point" && 
        Array.isArray(geoJSONGeometry.coordinates) && 
        geoJSONGeometry.coordinates.length === 2 &&
        geoJSONGeometry.coordinates[0] !== 0 &&
        geoJSONGeometry.coordinates[1] !== 0) {
      const [lng, lat] = geoJSONGeometry.coordinates;
      
      // Validate coordinate ranges
      if (typeof lng === 'number' && typeof lat === 'number' && 
          lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90) {
        center = geoJSONGeometry.coordinates;
        zoom = 14;
        hasValidCoordinates = true;
        console.log("[MAP-ZOOM] Valid Point coordinates:", { center, zoom });
      } else {
        console.warn("[MAP-ZOOM] Invalid coordinate ranges:", { lng, lat });
      }
    } 
    else if (geoJSONGeometry.type === "LineString" && 
             Array.isArray(geoJSONGeometry.coordinates) && 
             geoJSONGeometry.coordinates.length > 0) {
      // Find first non-zero coordinate
      const validPoint = geoJSONGeometry.coordinates.find((point: [number, number]) => 
        point[0] !== 0 || point[1] !== 0
      );
      if (validPoint) {
        center = validPoint;
        zoom = 12;
        hasValidCoordinates = true;
        console.log("[MAP-ZOOM] Valid LineString coordinates:", { center, zoom });
      }
    }
    else if (geoJSONGeometry.type === "MultiLineString" && 
             Array.isArray(geoJSONGeometry.coordinates) && 
             geoJSONGeometry.coordinates.length > 0) {
      // Extract all points and find first valid one
      for (const lineString of geoJSONGeometry.coordinates) {
        if (Array.isArray(lineString)) {
          const validPoint = lineString.find((point: [number, number]) => 
            Array.isArray(point) && point.length === 2 && (point[0] !== 0 || point[1] !== 0)
          );
          if (validPoint) {
            center = validPoint;
            zoom = 10;
            hasValidCoordinates = true;
            console.log("[MAP-ZOOM] Valid MultiLineString coordinates:", { center, zoom });
            break;
          }
        }
      }
    }
    else if (geoJSONGeometry.type === "Polygon" && 
             Array.isArray(geoJSONGeometry.coordinates[0]) && 
             geoJSONGeometry.coordinates[0].length > 0) {
      const firstPoint = geoJSONGeometry.coordinates[0][0];
      if (firstPoint[0] !== 0 || firstPoint[1] !== 0) {
        center = firstPoint;
        zoom = 10;
        hasValidCoordinates = true;
        console.log("[MAP-ZOOM] Valid Polygon coordinates:", { center, zoom });
      }
    }
    else if (geoJSONGeometry.type === "MultiPolygon" && 
             Array.isArray(geoJSONGeometry.coordinates) && 
             geoJSONGeometry.coordinates.length > 0 &&
             Array.isArray(geoJSONGeometry.coordinates[0]) &&
             geoJSONGeometry.coordinates[0].length > 0 &&
             Array.isArray(geoJSONGeometry.coordinates[0][0]) &&
             geoJSONGeometry.coordinates[0][0].length > 0) {
      const firstPoint = geoJSONGeometry.coordinates[0][0][0];
      if (firstPoint[0] !== 0 || firstPoint[1] !== 0) {
        center = firstPoint;
        zoom = 10;
        hasValidCoordinates = true;
        console.log("[MAP-ZOOM] Valid MultiPolygon coordinates:", { center, zoom });
      }
    }
  } catch (error) {
    console.error("[MAP-ZOOM] Error processing geometry:", error);
  }
  
  return { hasValidCoordinates, center, zoom };
};

/**
 * Create place data from cached item
 */
export const createPlaceDataFromCachedItem = (
  item: any, 
  placeId: string | number, 
  layerConfig: MapLayerConfig
): ZoomPlaceData | null => {
  const coordinates = extractCoordinatesFromItem(item);
  
  if (!coordinates) {
    return null;
  }
  
  console.log("[MAP-ZOOM] Creating place data from cached item:", {
    coordinates,
    longitude: coordinates[0],
    latitude: coordinates[1]
  });
  
  return {
    feature: {
      type: 'Feature',
      geometry: { 
        type: 'Point', 
        coordinates 
      },
      properties: { 
        ...item,
        id: placeId,
        name: item.name || item._title || item.title || `Item ${placeId}`
      },
      layer: { id: layerConfig.id }
    },
    center: coordinates,
    zoom: 14
  };
};

/**
 * Process API response to create zoom place data
 */
export const processApiResponseForZoom = (
  response: any, 
  layerConfig: MapLayerConfig
): ZoomPlaceData | null => {
  if (!response) return null;
  
  console.log("[MAP-ZOOM] Processing API response for zoom");
  
  // Parse geometry
  let geoJSONGeometry = null;
  if (response.geometry) {
    if (typeof response.geometry === 'string') {
      try {
        geoJSONGeometry = wktToGeoJSON(response.geometry);
        console.log("[MAP-ZOOM] Parsed WKT to GeoJSON:", geoJSONGeometry);
      } catch (error) {
        console.error("[MAP-ZOOM] Failed to parse WKT:", error);
        return null;
      }
    } else if (typeof response.geometry === 'object') {
      geoJSONGeometry = response.geometry;
    }
  }
  
  if (!geoJSONGeometry) {
    console.log("[MAP-ZOOM] No valid geometry found in API response");
    return null;
  }
  
  // Validate and extract coordinates
  const { hasValidCoordinates, center, zoom } = validateAndProcessGeometry(geoJSONGeometry);
  
  if (!hasValidCoordinates) {
    console.log("[MAP-ZOOM] No valid coordinates found in geometry");
    return null;
  }
  
  return {
    feature: {
      type: 'Feature',
      geometry: geoJSONGeometry,
      properties: response,
      layer: { id: layerConfig.id }
    },
    center,
    zoom
  };
};

/**
 * Find and zoom to a place by ID with optional popup display
 * @param placeId - The ID of the place to find
 * @param layerId - The layer ID to search in
 * @param layers - Available map layers
 * @param mapRef - Map reference
 * @param config - App configuration
 * @param searchableItemsData - Cached search data
 * @param handleClick - Optional click handler for popup display
 * @param setIsLoading - Optional loading state setter
 */
export const findAndZoomToPlaceById = async (
  placeId: string | number,
  layerId: string,
  layers: MapLayerConfig[],
  mapRef: any,
  config: any,
  searchableItemsData: Record<string, any[]>,
  userProfile: any,
  handleClick?: (event: any) => void,
  setIsLoading?: (loading: boolean) => void
): Promise<void> => {
  const map = mapRef.current?.getMap();
  if (!map) {
    throw new Error("Map not initialized");
  }
  
  // Find layer with flexible matching
  const layerConfig = findLayerWithFlexibleMatching(layers, layerId);
  if (!layerConfig) {
    const availableLayers = layers.map(l => l.id).slice(0, 3).join(", ");
    throw new Error(`Layer not found: ${layerId}. Available: ${availableLayers}...`);
  }
  
  setIsLoading?.(true);
  
  try {
    let placeData: ZoomPlaceData | null = null;
    
    // Try cached data first for better performance
    if (searchableItemsData[layerConfig.id]) {
      const item = searchableItemsData[layerConfig.id].find(item => 
        String(item.id) === String(placeId)
      );
      
      if (item) {
        placeData = createPlaceDataFromCachedItem(item, placeId, layerConfig);
        if (placeData) {
          console.log("[MAP-ZOOM] Using cached data for zoom");
        }
      }
    }
    
    // Fetch from API if no cached data available
    if (!placeData) {
      // Check if this is a Fulcrum layer - if so, skip Django API calls since they expect numeric IDs
      const isFulcrumLayer = layerConfig.type === 'fulcrum';
      
      // DEBUG: Log layer detection to understand the routing issue
      const placeIdIsUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(placeId));
      console.log('[MAP-ZOOM] DEBUG - Layer detection for place fetch:', {
        placeId,
        layerId,
        layerConfigId: layerConfig.id,
        layerConfigType: layerConfig.type,
        isFulcrumLayer,
        placeIdIsUUID
      });
      
      // SMART ROUTING: If the placeId is a UUID but layer type is not 'fulcrum', 
      // it's likely a Fulcrum entity with incorrect layer config
      const shouldUseFulcrumRouting = isFulcrumLayer || placeIdIsUUID;
      
      if (!shouldUseFulcrumRouting) {
        // Find API endpoint for non-Fulcrum layers
        const apiEndpointConfig = config.apiEndpoints?.find((endpoint: any) => 
          endpoint.mapLayerId === layerConfig.id || endpoint.tableName === layerId
        );
        
        if (apiEndpointConfig) {
          try {
            const response = await apiFetch(`${REACT_APP_API_ROOT}${apiEndpointConfig.api_endpoint}${placeId}/`);
            placeData = processApiResponseForZoom(response, layerConfig);
          } catch (error) {
            console.warn("[MAP-ZOOM] Direct API fetch failed:", error);
          }
        }
        
        // Fallback to generic search endpoint for non-Fulcrum layers
        if (!placeData) {
          try {
            const apiEndpoint = layerConfig.searchApiEndpoint || 'api/user/places';
            const fetchedData = await fetchPlaceById(placeId, apiEndpoint, layerConfig.id, userProfile);
            if (fetchedData) {
              placeData = fetchedData as ZoomPlaceData;
            }
          } catch (error) {
            console.warn("[MAP-ZOOM] Fallback fetch failed:", error);
          }
        }
      } else {
        // Handle Fulcrum layers (or UUID-based entities) using the specialized fetchPlaceById function
        console.log("[MAP-ZOOM] Fulcrum routing detected, using specialized handler");
        console.log("[MAP-ZOOM] Routing reason:", isFulcrumLayer ? "layer type is 'fulcrum'" : "placeId is UUID format");
        try {
          // Use a Fulcrum-specific endpoint that will trigger the Fulcrum handling in fetchPlaceById
          const fulcrumEndpoint = 'api/fulcrum/records';
          const fetchedData = await fetchPlaceById(placeId, fulcrumEndpoint, layerConfig.id, userProfile);
          if (fetchedData) {
            placeData = fetchedData as ZoomPlaceData;
            console.log("[MAP-ZOOM] Successfully fetched Fulcrum data for zoom");
          }
        } catch (error) {
          console.warn("[MAP-ZOOM] Fulcrum fetch failed:", error);
        }
      }
    }
    
    if (!placeData) {
      throw new Error(`Failed to fetch data for place ${placeId}`);
    }
    
    // Parse any remaining WKT geometry strings
    if (typeof placeData.feature.geometry === 'string') {
      try {
        const parsedGeometry = wktToGeoJSON(placeData.feature.geometry);
        if (parsedGeometry) {
          placeData.feature.geometry = parsedGeometry;
          
          // Update center for Point geometries
          if (parsedGeometry.type === 'Point' && 'coordinates' in parsedGeometry) {
            placeData.center = [parsedGeometry.coordinates[0], parsedGeometry.coordinates[1]];
          }
        }
      } catch (error) {
        console.error("[MAP-ZOOM] Failed to parse remaining WKT:", error);
      }
    }
    
    // Skip invalid coordinates
    if (placeData.center[0] === 0 && placeData.center[1] === 0) {
      console.log("[MAP-ZOOM] Skipping zoom - invalid coordinates [0,0]");
      setIsLoading?.(false);
      return;
    }
    
    // Execute zoom with fitBounds for all geometry types
    return new Promise<void>((resolve) => {
      const moveEndListener = () => {
        // Create popup event only if handleClick handler is provided
        if (handleClick) {
          const mockEvent = {
            features: [{
              ...placeData!.feature,
              layer: { id: layerConfig.id },
              properties: placeData!.feature.properties
            }],
            lngLat: { lng: placeData!.center[0], lat: placeData!.center[1] },
            type: 'click',
            target: map
          };
          
          handleClick(mockEvent);
        }
        setIsLoading?.(false);
        resolve();
      };
      
      map.once('moveend', moveEndListener);
      
      // Use fitBounds for all geometry types (works for points, lines, polygons)
      if (placeData.feature.geometry) {
        try {
          const boundingBox = bbox(placeData.feature.geometry);
          
          // Check if this is a Point geometry that should get tilt
          const isPoint = placeData.feature.geometry.type === 'Point';
          const maxZoom = 14;
          const shouldApplyTilt = isPoint && maxZoom > 13.5;
          
          console.log('[MAP-ZOOM] findAndZoomToPlaceById geometry decision:', {
            geometryType: placeData.feature.geometry.type,
            isPoint,
            maxZoom,
            shouldApplyTilt,
            coordinates: placeData.feature.geometry.coordinates
          });
          
          map.fitBounds([
            [boundingBox[0], boundingBox[1]], // southwest
            [boundingBox[2], boundingBox[3]]  // northeast
          ], {
            padding: 50,
            maxZoom,
            pitch: shouldApplyTilt ? 60 : 0, // Add tilt for Point geometries
            duration: 2000
          });
        } catch (error) {
          console.error('[MAP-ZOOM] Error with bbox/fitBounds, falling back to flyTo:', error);
          // Fallback to simple flyTo
          map.flyTo({
            center: placeData.center,
            zoom: placeData.zoom,
            duration: 2000,
            essential: true
          });
        }
      } else {
        // Fallback for coordinates without geometry
        map.flyTo({
          center: placeData.center,
          zoom: placeData.zoom,
          duration: 2000,
          essential: true
        });
      }
    });
    
  } catch (error) {
    console.error("[MAP-ZOOM] Error in findAndZoomToPlaceById:", error);
    setIsLoading?.(false);
    throw error;
  }
};

/**
 * Zoom to coordinates directly with smart zoom logic
 */
export const zoomToCoordinates = (
  coordinates: [number, number],
  mapRef: any,
  handleClick?: (event: any) => void
): void => {
  const [lng, lat] = coordinates;
  
  if (!lng && !lat) {
    console.log("[MAP-ZOOM] Invalid coordinates - skipping zoom");
    return;
  }
  
  // Validate coordinate ranges
  if (typeof lng !== 'number' || typeof lat !== 'number' || 
      lng < -180 || lng > 180 || lat < -90 || lat > 90) {
    console.warn("[MAP-ZOOM] Invalid coordinate ranges:", { lng, lat });
    return;
  }
  
  console.log("[MAP-ZOOM] Zooming to coordinates:", { lng, lat });
  
  const map = mapRef.current?.getMap();
  if (!map) {
    console.error("[MAP-ZOOM] Map not available");
    return;
  }
  
  if (handleClick) {
    map.once('moveend', () => {
      console.log("[MAP-ZOOM] Coordinate zoom complete");
      
      const mockEvent = {
        features: [{
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: { name: `Location ${lat.toFixed(5)}, ${lng.toFixed(5)}` },
          layer: { id: 'coordinates' }
        }],
        lngLat: { lng, lat },
        type: 'click',
        target: map
      };
      
      handleClick(mockEvent);
    });
  }
  
  // Use smart zoom logic
  smartZoomToLocation({
    center: [lng, lat],
    map,
    duration: 2000,
    defaultZoom: 14,
    fromMapClick: false // Coordinate searches are intentional navigation
  });
}; 